import './DatePicker.css';

import dayjs from 'dayjs';
import React from 'react';

import Calendar from '../Calendar/Calendar';
import classNames from 'classnames';
import useToggle from '../../hooks/useToggle';
import { Input } from '../../flayr-components/Input/Input';
import { ShowIf } from '../../flayr-components/ShowIf/ShowIf';

interface IDatePickerProps {
  name?: string;
  value?: string;
  label?: string;
  id?: string;
  inputContainerId?: string;
  inputClassName?: string;
  inputClassNameWhenCalendarOpen?: string;
  resetInputClassName?: boolean;
  position?: 'absolute' | 'fixed';
  onChange?: (e?: any) => void;
  leftIcon?: any;
  placeholder?: string;
  mb?: string;
  closeOnBodyClick?: boolean;
  enablePastDates?: boolean;
}

const DatePicker: React.FC<React.PropsWithChildren<IDatePickerProps>> = ({
  name,
  value = '',
  label,
  id,
  inputContainerId,
  inputClassName,
  resetInputClassName,
  inputClassNameWhenCalendarOpen = '',
  position = 'fixed',
  leftIcon,
  placeholder,
  closeOnBodyClick,
  onChange,
  mb,
  enablePastDates,
}) => {
  const calendarContainerRef = React.useRef<HTMLDivElement>(null);
  const calendar = useToggle();
  const [dimensions, setDimensions] = React.useState<any>(undefined);

  function handleDateSelect(date) {
    onChange?.({ target: { name, value: date } });
    calendar.toggle();
  }

  React.useEffect(() => {
    const el = calendarContainerRef.current;
    const bb = el?.getBoundingClientRect();
    setDimensions({
      '--left': `${bb?.left || 0}px`,
      '--top': `${bb?.top || 0}px`,
      '--width': `${bb?.width || 0}px`,
    });
  }, []);

  React.useEffect(() => {
    if (calendar.opened && closeOnBodyClick) {
      const listener = calendar.toggle;
      document.addEventListener('click', listener);
      return () => {
        document.removeEventListener('click', listener);
      };
    }
  }, [calendar.opened, closeOnBodyClick]);

  function handleContainerClick(e) {
    if (closeOnBodyClick) {
      e.preventDefault();
    }
  }

  return (
    <div className="DatePicker">
      <div
        className="overflow-hidden md:cursor-pointer"
        id={inputContainerId}
        onClick={calendar.toggle}
      >
        <Input
          label={label}
          id={id}
          name={name}
          size="lg"
          value={(value && dayjs(value).format('ddd, DD MMM YYYY')) || ''}
          className="pointer-events-none"
          leftIcon={leftIcon}
          placeholder={placeholder}
          resetInputClassName={resetInputClassName}
          inputClassName={classNames(
            {
              'pl-8': !!leftIcon,
              [inputClassNameWhenCalendarOpen]:
                calendar.opened && !!inputClassNameWhenCalendarOpen,
            },
            'rounded-lg',
            inputClassName
          )}
          mb={mb}
        />
      </div>
      <ShowIf condition={position === 'fixed'}>
        <div ref={calendarContainerRef} onClick={handleContainerClick}>
          {calendar.opened && (
            <>
              <div
                className="fixed top-0 left-0 z-10 w-full h-full"
                onClick={calendar.toggle}
              />
              <div
                className="fixed z-20 p-3 bg-white rounded-lg shadow-lg DatePicker-calendar"
                style={dimensions as React.CSSProperties}
              >
                <Calendar
                  date={value || ''}
                  className="w-full"
                  selectedDates={[value]}
                  onDateSelect={handleDateSelect}
                  enablePastDates={enablePastDates}
                />
              </div>
            </>
          )}
        </div>
      </ShowIf>

      <ShowIf condition={position === 'absolute'}>
        <div className="relative z-10 w-full" onClick={handleContainerClick}>
          <div
            hidden={calendar.closed}
            className="absolute top-0 left-0 pt-1"
            style={{ width: `calc(100vw - 4rem)`, maxWidth: 500 }}
          >
            <div className="p-2 py-3 bg-white border rounded-lg shadow-lg">
              <Calendar
                date={value || ''}
                className="w-full"
                selectedDates={[value]}
                onDateSelect={handleDateSelect}
                enablePastDates={enablePastDates}
              />
            </div>
          </div>
        </div>
      </ShowIf>
    </div>
  );
};
export default DatePicker;
